import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { Box, Button } from '@mui/material';
import { palette } from 'theme/palette';
import { closeModal } from 'reducers/modalReducer';
import { setTermsAndCondition } from 'reducers/generateCodeDetailsReducer';
import {
  generateMemberCodeThunk,
  setDistributionRequest,
  setGenerateCodeError,
} from 'reducers/memberCodeStatusReducer';
import { selectPartnerId } from 'selectors/partnerStatusSelector';
import { selectGenerateCodeDetails, selectIsTermsAndConditionAccepted } from 'selectors/generateCodeDetailsSelector';
import {
  selectDistributionRequest,
  selectGenerateCodeError,
  selectIsGenerateCodeLoading,
} from 'selectors/memberCodeStatuSelector';
import { LoadingButton } from 'components/common/LoadingButton';
import { CustomError } from 'types';
import { GenerateEmailResponse } from 'pco-product-service-internal-consumer';
import { useErrorHandling } from 'components/hooks/useErrorHandling';
enum MemberZoneSubjects {
  EN = 'Julius Baer Mobile App - Insights Activation Code',
  ES = 'Julius Baer Mobile App - Código de activación de Insights',
  DE = 'Julius Bär Mobile App - Insights Aktivierungscode',
  FR = "Julius Baer Mobile App - Code d'activation de Insights",
  IT = 'Julius Baer Mobile App - Codice di attivazione per Insights',
}
import { PRODUCTS_AND_SERVICES } from 'services/constants';
import { AppDispatch, RootState } from 'store';
import { videoIdentificationStyles } from 'views/VideoIdentification/VideoIdentificationFormActions';

export const CreateAccessCodeActions = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const partnerNumber = useSelector(selectPartnerId);
  const isTCAccepted = useSelector(selectIsTermsAndConditionAccepted);
  const requestBody = useSelector(selectGenerateCodeDetails);
  const isCreateCodeButtonDisabled = !isTCAccepted;
  const isLoading = useSelector(selectIsGenerateCodeLoading);
  const error: CustomError | undefined = useSelector(selectGenerateCodeError);
  const distributionRequest: GenerateEmailResponse | undefined = useSelector(selectDistributionRequest);
  const CLM_URL = useSelector((state: RootState) => state.appConfig.CLM_URL);

  const handleCancel = () => {
    dispatch(setTermsAndCondition(false));
    navigate(`/partner?partnerNumber=${partnerNumber}`);
  };

  useErrorHandling(error, () => {
    navigate(`/partner?partnerNumber=${partnerNumber}`);
    dispatch(setGenerateCodeError(undefined));
    dispatch(closeModal());
  });

  useEffect(() => {
    if (
      distributionRequest &&
      distributionRequest.distributionRequestId &&
      distributionRequest.userId &&
      distributionRequest.partnerUid
    ) {
      const { distributionRequestId } = distributionRequest;
      const topic = 'JB_OFFERING';
      const subject = MemberZoneSubjects[requestBody.partnerEmail?.language ?? 'EN'];
      window.parent.postMessage(
        {
          message: 'redirectDocumentTab_V2',
          value: {
            distributionRequestId,
            topic,
            subject,
            initiator: PRODUCTS_AND_SERVICES,
          },
        },
        CLM_URL,
      );
      navigate(`/partner?partnerNumber=${partnerNumber}`);
      dispatch(setDistributionRequest(undefined));
    }
  }, [distributionRequest, dispatch, partnerNumber, requestBody.partnerEmail?.language, navigate, CLM_URL]);

  const handleGenerateCodeRequest = () => {
    if (partnerNumber) {
      dispatch(generateMemberCodeThunk({ partnerNumber, requestBody }));
    }
  };

  return (
    <Box sx={videoIdentificationStyles.actions}>
      <Box
        style={{
          width: '95%',
          display: 'flex',
          justifyContent: 'space-between',
          maxWidth: '95%',
        }}
      >
        <Button
          id="cancel-member-code-button"
          data-testid="cancel-member-code-button"
          onClick={handleCancel}
          variant="outlined"
          style={{ color: palette.black }}
        >
          Cancel
        </Button>
        <Box
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <LoadingButton
            style={{ marginRight: 0 }}
            id="create-member-code-button"
            data-testid="create-member-code-button"
            variant="contained"
            onClick={handleGenerateCodeRequest}
            color="primary"
            loading={isLoading}
            disabled={isCreateCodeButtonDisabled}
          >
            Create code
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  );
};
